import { SpeechError, SpeechErrorCode } from './errors'
import Resampler from './resampler'
import { encodeWav, mergeFloat32 } from './wav'

/** 厂商统一要 16kHz / 16bit / 单声道 */
export const TARGET_SAMPLE_RATE = 16000

const BUFFER_LEN = 4096

/**
 * 录音器：采集麦克风 → 重采样到 16kHz → 攒成整段 WAV。
 *
 * 与被替换掉的讯飞录音器不同，这里不做分片上行（Azure / SpeechSuper 都是一次性整段音频的
 * HTTP 接口），因此只保留采集、波形、导出 WAV 三件事。
 */
export default class SpeechRecorder {
  constructor(options = {}) {
    this.wave_callback = options.wave_callback || null

    this.audioContext = null
    this.mediaStream = null
    this.mediaSource = null
    this.scriptProcessor = null
    this.analyserNode = null
    this.resampler = null

    this.waveArray = null
    this.waveTimer = null

    this.chunks = []
    this.sampleCount = 0
    this.recording = false

    this.startedAt = 0
    this.durationMs = 0

    this.initPromise = null
    this.destroyed = false
  }

  /** 是否已经拿到麦克风、可以直接 start */
  get ready() {
    return !!this.scriptProcessor
  }

  /**
   * 申请麦克风并建好音频链路。重复调用复用已有链路；
   * 授权弹窗还开着时再次调用会复用同一个 promise——否则会二次 getUserMedia，
   * 弹两次授权框，且第一条 MediaStream 再也没人关（地址栏录音标记常亮）。
   *
   * @throws {SpeechError} NO_GET_USER_MEDIA / NO_WEB_AUDIO / MIC_DENIED
   */
  init() {
    if (this.destroyed || this.ready) return Promise.resolve()
    if (this.initPromise) return this.initPromise
    this.initPromise = this.__doInit().finally(() => {
      this.initPromise = null
    })
    return this.initPromise
  }

  async __doInit() {
    // 非安全上下文（http 且非 localhost）下 navigator.mediaDevices 是 undefined
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new SpeechError(
        SpeechErrorCode.NO_GET_USER_MEDIA,
        '浏览器无法录音：需要在 https、localhost 或 127.0.0.1 下访问'
      )
    }

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext
    if (!AudioContextCtor) {
      throw new SpeechError(SpeechErrorCode.NO_WEB_AUDIO, '浏览器不支持 Web Audio')
    }

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    } catch (e) {
      // errorName 留给 UI 区分"没授权/没设备"和"设备被占用"，沿用旧录音器的提示分支
      throw new SpeechError(SpeechErrorCode.MIC_DENIED, (e && e.message) || '获取麦克风失败', {
        errorName: e && e.name,
      })
    }
    // 等授权这段时间里组件可能已经卸载，此时必须把刚拿到的流关掉，否则麦克风一直开着
    if (this.destroyed) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }

    let audioContext
    try {
      audioContext = new AudioContextCtor()
      await audioContext.resume()
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop())
      throw new SpeechError(
        SpeechErrorCode.NO_WEB_AUDIO,
        (e && e.message) || '创建 AudioContext 失败'
      )
    }
    if (this.destroyed) {
      stream.getTracks().forEach((t) => t.stop())
      audioContext.close()
      return
    }

    this.audioContext = audioContext
    this.mediaStream = stream
    this.analyserNode = this.audioContext.createAnalyser()
    this.analyserNode.fftSize = 2048
    this.analyserNode.minDecibels = -90
    this.analyserNode.maxDecibels = -10
    this.analyserNode.smoothingTimeConstant = 0.85
    this.waveArray = new Uint8Array(this.analyserNode.frequencyBinCount)

    this.scriptProcessor = this.audioContext.createScriptProcessor(BUFFER_LEN, 1, 1)
    // 按设备真实采样率建重采样器。outputBufferSize 同时被 Resampler 用来 clamp 输入读取长度，
    // 所以下界必须是输入帧长；设备采样率低于 16k（蓝牙耳机进 HFP 通话模式会给 8k）时要升采样，
    // 输出槽位得更大，不放大就会静默丢掉一半采样，录音变成 2 倍速。
    this.resampler = new Resampler(
      this.audioContext.sampleRate,
      TARGET_SAMPLE_RATE,
      1,
      Math.max(
        this.scriptProcessor.bufferSize,
        Math.ceil((this.scriptProcessor.bufferSize * TARGET_SAMPLE_RATE) / this.audioContext.sampleRate) + 1
      )
    )
    this.scriptProcessor.onaudioprocess = (e) => {
      if (!this.recording) return
      const input = e.inputBuffer.getChannelData(0)
      const resampled =
        this.audioContext.sampleRate === TARGET_SAMPLE_RATE ? input : this.resampler.resampler(input)
      // resampler 复用同一块输出缓冲，必须拷贝一份再存
      this.chunks.push(new Float32Array(resampled))
      this.sampleCount += resampled.length
    }

    this.mediaSource = this.audioContext.createMediaStreamSource(stream)
    this.mediaSource.connect(this.scriptProcessor)
    this.mediaSource.connect(this.analyserNode)
  }

  /** 开始录音（会清掉上一段） */
  start() {
    if (!this.ready) return
    this.chunks = []
    this.sampleCount = 0
    this.durationMs = 0
    this.recording = true
    this.startedAt = Date.now()

    if (this.audioContext.state === 'suspended') this.audioContext.resume()
    this.scriptProcessor.connect(this.audioContext.destination)
    this.__startWave()
  }

  /** 结束录音，返回本段时长（毫秒） */
  stop() {
    if (!this.recording) return this.durationMs
    this.recording = false
    this.durationMs = Date.now() - this.startedAt
    try {
      this.scriptProcessor.disconnect()
    } catch (e) {
      // 已经断开时再断会抛，忽略
    }
    this.__stopWave()
    return this.durationMs
  }

  /** 导出整段录音；没录到东西返回 null */
  exportWav() {
    if (!this.sampleCount) return null
    return encodeWav(mergeFloat32(this.chunks, this.sampleCount), TARGET_SAMPLE_RATE)
  }

  /** 本段时长（毫秒） */
  getDurationMs() {
    return this.recording ? Date.now() - this.startedAt : this.durationMs
  }

  /** 彻底释放：停录音、关麦克风、销毁 AudioContext */
  destroy() {
    this.destroyed = true
    this.stop()

    if (this.mediaSource) {
      try {
        this.mediaSource.disconnect()
      } catch (e) {
        // 忽略重复断开
      }
      this.mediaSource = null
    }
    if (this.scriptProcessor) {
      this.scriptProcessor.onaudioprocess = null
      this.scriptProcessor = null
    }
    if (this.mediaStream) {
      // 不 stop track 的话浏览器地址栏的录音标记会一直亮着
      this.mediaStream.getTracks().forEach((t) => t.stop())
      this.mediaStream = null
    }
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    this.analyserNode = null
    this.chunks = []
    this.sampleCount = 0
  }

  __startWave() {
    if (!this.wave_callback) return
    this.__stopWave()
    this.waveTimer = setInterval(() => {
      this.analyserNode.getByteTimeDomainData(this.waveArray)
      this.wave_callback(this.waveArray)
    }, 50)
  }

  __stopWave() {
    if (this.waveTimer) {
      clearInterval(this.waveTimer)
      this.waveTimer = null
    }
  }
}
